<script>
  import { getContext } from "svelte";
  import { navigate } from "svelte-routing";
  import cloneDeep from "lodash/cloneDeep";
  import SidebarSection from "./SidebarSection.svelte";
  import TextInput from "./TextInput.svelte";
  import ButtonInput from "./ButtonInput.svelte";
  import AiIcon from "@local/assets/icons/ai.svg";
  import CircleCheckIcon from "@local/assets/icons/circle-check-filled.svg";
  import ImageIcon from "@local/assets/icons/image.svg";
  import NewFileIcon from "@local/assets/icons/new-file.svg";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import makeMultiCategory from "#src/extensions/multi-category.js";
  import { scaleOptions, translations, definedMultipliers } from "@local/extensions/utilities/scales.js";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";
  import { locRecord } from "#src/extensions/filter-items.js";
  import { analyzePagesParallel } from "#src/api/ai.js";
  import { pageAnalysisLlmModel, pageAnalysisReasoningLevel } from "#src/stores/ui.js";
  import pageImage from "#src/extensions/page-image.js";
  import { alerts } from "#src/stores/alerts.js";
  import extension from "@local/extensions/utilities/extension.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import { fetchShpxAssistData, updateShpxAssistData } from "#src/extensions/shpx-assist-data.js";
  import analyzePages from "#src/extensions/analyze-pages.js";
  import { profile } from "#src/stores/auth.js";
  import { UX_MODE } from "#src/env.js";
  import { setPageFlags } from "#lib/framex/page-flags.js";

  export let pages;
  export let group;
  export let origDoc;
  export let doc;
  export let indexedLocations;
  export let disabled = false;
  export let isVirtual;
  // FrameX: move the "Page" heading to the very top of the section. Defaults
  // false, so the legacy panel (Actions first, then the Page heading) is
  // unchanged.
  export let framex = false;

  const org = getContext("org");
  const shpxAssistPages = getContext("shpxAssistPages");

  $: page = pages.length === 1 ? pages[0] : makeMultiCategory(pages);
  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: allScaleOptions = [
    { label: "None", value: null },
    ...(UX_MODE === "shpx" ? [{ label: "N/A — not a scaled drawing", value: "__na__" }] : []),
    { label: "Set Custom Scale", value: "custom", unclickable: true },
    ...scaleOptions,
    ...currentPageScale(page),
  ];
  $: assistStatus = getAssistStatus(pages, $shpxAssistPages[origDoc?.id] || {});

  // shpx-only: an N/A page carries `scale: null` alongside the notApplicable flag,
  // so binding the dropdown to `scale` alone showed "None" and N/A could never
  // appear as the current choice — which also made the state look unchangeable.
  // Only when every selected page agrees; a mixed set falls back to the scale, the
  // same "don't guess" rule the rest of this panel follows. Reads the flag directly
  // rather than via derive.js's helper, which `updateScale` shadows with a local.
  $: scaleSelectValue =
    UX_MODE === "shpx" && pages.length && pages.every((p) => p.notApplicable) ? "__na__" : page?.scale;

  let requestingAiAnalysis = false;
  let customScaleModal;

  function updatePage(prop, value) {
    const data = cloneDeep(doc.data);
    pages.forEach((p) => {
      const page = data.pages[p.id];
      page[prop] = value;
    });
    group.updateDocument(doc.id, { data });
  }

  function getAssistStatus(pages, assistPages) {
    if (!assistPages) return null;
    if (!pages?.length) return null;
    const [first, ...rest] = pages;
    let status = assistPages[first.id]?.status || null;
    rest.forEach((p) => {
      const s = assistPages[p.id]?.status || null;
      if (s !== status) {
        status = "mixed";
      }
    });
    return status;
  }

  function currentPageScale(page) {
    if (!page) return [];
    if (!page.scale) return [];
    if (definedMultipliers[page.scale]) return [];
    return [{ label: `Custom Scale: ${page.scale}`, value: page.scale }];
  }

  function updateSection(e) {
    const val = e.detail.value;
    if (!val) {
      updatePage("section", null);
    } else {
      updatePage("section", val);
    }
  }

  function setDocThumbnail() {
    const selected = pages[0];
    if (!selected) return;
    const ext = extension(selected.name).toLowerCase();

    let thumbnail;
    if (ext === "pdf") {
      const id = selected.name.split("/")[0];
      thumbnail = {
        objectName: selected.name,
        bucket: "drawings",
        type: "iiif",
        name: doc.name,
        path: `${id}/doc.${selected.page}.tiff`,
      };
    } else if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
      thumbnail = {
        type: "image",
        bucket: "documents",
        name: doc.name,
        path: selected.name,
      };
    }

    if (!thumbnail) return;

    if (isVirtual) {
      group.updateDocument(origDoc.id, {
        data: {
          ...origDoc.data,
          thumbnail,
        },
      });
    } else {
      group.updateDocument(doc.id, {
        data: {
          ...doc.data,
          thumbnail,
        },
      });
    }
    alerts.add({ type: "ok", message: "Document thumbnail updated." });
  }

  async function analyze() {
    requestingAiAnalysis = true;
    const data = await analyzePages(
      pages,
      doc,
      $org.id,
      $pageAnalysisLlmModel,
      $pageAnalysisReasoningLevel,
      $profile?.user_role === "developer",
    );

    if (data) {
      group.updateDocument(doc.id, { data });
    }

    requestingAiAnalysis = false;
  }

  function createDoc(groupId, index) {
    return {
      id: crypto.randomUUID(),
      group_id: groupId,
      index,
      name: "Untitled",
      ready: true,
      data: {
        pages: null,
        thumbnail: {},
      },
      is_virtual: true,
      type: "virtual",
      source_document_id: null,
    };
  }

  function defaultFilters() {
    return {
      show_items: true,
      show_collections: true,
      show_types: true,
      show_notes: true,
      show_dimensions: true,
      location_filter: null,
      item_filter: null,
      collection_filter: null,
      type_filter: null,
      note_filter: null,
      hidden_pages: {},
      hidden_locations: {},
    };
  }

  async function createWorkingDoc() {
    const newDoc = createDoc($group.id, $group.documents.order.length);
    newDoc.source_document_id = doc.id;
    newDoc.name = nameMinusExtension(doc.name);
    const firstPage = pages[0];
    const ext = extension(firstPage.name);
    let thumbnail;
    if (ext === "pdf") {
      const id = firstPage.name.split("/")[0];
      thumbnail = {
        bucket: "drawings",
        name: firstPage.label || "",
        objectName: firstPage.name,
        path: `${id}/doc.${firstPage.page}.tiff`,
        type: "iiif",
      };
    } else {
      thumbnail = {
        bucket: "documents",
        path: firstPage.name,
        type: "image",
        name: firstPage.label || "",
      };
    }

    const filters = defaultFilters();
    const visible = pages.reduce((acc, p) => {
      acc[p.id] = true;
      return acc;
    }, {});
    doc.data.pages.order.forEach((pId) => {
      if (!visible[pId]) {
        filters.hidden_pages[pId] = true;
      }
    });
    newDoc.data.thumbnail = thumbnail;
    newDoc.data.filters = filters;
    await group.addDocument(newDoc);
    navigate(`/jobs/${$group.job_id}/documents/${newDoc.id}`);
  }

  async function pageAction(action) {
    if (action === "analyze") {
      analyze();
    } else if (action === "set-thumbnail") {
      setDocThumbnail();
    } else if (action === "create-working-doc") {
      createWorkingDoc();
    }
  }

  function updateScale(e) {
    let val = e.detail.value;
    // shpx-only: "N/A" clears the scale like "None" but also marks the
    // pages skipped-on-purpose, so it must be told apart from a real
    // clear before falling through to the shared write path below.
    const isNotApplicable = UX_MODE === "shpx" && val === "__na__";
    if (isNotApplicable) val = null;
    if (!val) val = null;

    const data = cloneDeep(doc.data);
    pages.forEach((p) => {
      const page = data.pages[p.id];
      page.scale = val;
    });

    const updates = [{ type: "document", id: doc.id, diff: { data } }];

    pages.forEach((p) => {
      const pageId = p.id;
      const locs = indexedLocations[pageId] || [];

      locs.forEach((loc) => {
        if (loc.clip && !loc.clip.drawing_scale) {
          const clip = { ...loc.clip, drawing_scale: val };
          updates.push({
            type: "location",
            id: loc.id,
            diff: { clip },
          });

          const record = locRecord(loc, $group);
          if (record?.drawing?.attachment_id === loc.id) {
            const drawing = cloneDeep(record.drawing);
            drawing.clip = clip;
            updates.push({
              type: loc.record_type === "collection" ? "item" : loc.record_type,
              id: record.id,
              diff: { drawing },
            });
          }
        }
      });
    });

    group.update(updates);

    if (UX_MODE === "shpx") {
      const pageIds = pages.map((p) => p.id);
      if (isNotApplicable) {
        setPageFlags(group, doc, pageIds, { notApplicable: true });
      } else {
        // Unconditional, "None" included. None means "no scale yet", which is a
        // different state from "not a scaled drawing", so choosing it has to return
        // the page to NO SCALE. Guarding this on a non-null scale made N/A a
        // one-way door: the page kept reading N/A whatever the dropdown said.
        setPageFlags(group, doc, pageIds, { notApplicable: false });
      }
    }
  }

  async function updateAssistStatus(status) {
    await updateShpxAssistData(
      pages.map((p) => p.id),
      {
        organization_id: $org.id,
        job_id: $group.job_id,
        document_id: origDoc.id,
        status,
      },
    );
    const data = await fetchShpxAssistData($group.job_id);
    shpxAssistPages.set(data);
  }
</script>

{#if framex}
  <SidebarSection>
    <h3 class="px-2 mb-2">Properties</h3>
  </SidebarSection>
{/if}
<SidebarSection>
  {#key pages}
    <ButtonInput
      {disabled}
      label="Actions"
      labelWidth="5.5rem"
      value={true}
      buttons={[
        ...(pages.length === 1
          ? [
              {
                component: ImageIcon,
                label: "Set Document Thumbnail",
                value: "set-thumbnail",
              },
            ]
          : []),
        {
          component: NewFileIcon,
          label: "Create Working Document from Pages",
          value: "create-working-doc",
        },
        {
          component: requestingAiAnalysis ? SpinnerIcon : AiIcon,
          label: "Analyze Pages",
          value: "analyze",
          disabled: requestingAiAnalysis,
          spin: requestingAiAnalysis,
        },
      ]}
      on:input={(e) => pageAction(e.detail.value)} />
  {/key}
</SidebarSection>
<SidebarSection>
  <div class="flex items-center justify-between">
    {#if !framex}
      {#if pages.length > 1}
        <h3 class="px-2 mb-2">Page ({pages.length} selected)</h3>
      {:else}
        <h3 class="px-2 mb-2">Page</h3>
      {/if}
    {/if}
  </div>
  <TextInput
    {disabled}
    labelWidth="5.5rem"
    label="Sheet Type"
    value={page?.section}
    on:input={updateSection} />
  <SelectInput
    label="Drawing Scale"
    labelWidth="5.5rem"
    options={allScaleOptions}
    value={scaleSelectValue}
    on:input={updateScale}
    on:option-click={() => customScaleModal.open()} />
  {#if capabilities.edit_tally_jobs && assistStatus}
    <SelectInput
      label="SHPX Status"
      labelWidth="5.5rem"
      value={assistStatus}
      options={[
        { label: "None", value: null },
        { label: "Submitted", value: "submitted" },
        { label: "Complete", value: "complete" },
        { label: "Flagged", value: "flagged" },
        { label: "Canceled", value: "canceled" },
        { label: "Acknowledged", value: "acknowledged" },
      ]}
      on:input={(e) => updateAssistStatus(e.detail.value)} />
  {:else if assistStatus && assistStatus !== "acknowledged"}
    <ButtonInput
      label="SHPX Status"
      labelWidth="5.5rem"
      value={true}
      buttons={[{ label: "Acknowledge", component: CircleCheckIcon, value: "acknowledge" }]}
      on:input={() => updateAssistStatus("acknowledged")} />
  {/if}
</SidebarSection>
<CustomScaleModal bind:this={customScaleModal} on:setScale={updateScale} />
